import { ChevronDown } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

interface RangeSelectorProps {
  min?: number;
  max?: number;
  step?: number;
  value?: [number, number];
  onChange?: (range: [number, number]) => void;
  label?: string;
  currencySymbol?: string;
}

const RangeSelector: React.FC<RangeSelectorProps> = ({
  min = 0,
  max = 10000,
  step = 100,
  value = [min, max],
  onChange,
  label = 'Amount Range',
  currencySymbol = '₹',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minValue, setMinValue] = useState(value[0]);
  const [maxValue, setMaxValue] = useState(value[1]);
  const [tempMinValue, setTempMinValue] = useState(value[0]);
  const [tempMaxValue, setTempMaxValue] = useState(value[1]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMinValue(value[0]);
    setMaxValue(value[1]);
    setTempMinValue(value[0]);
    setTempMaxValue(value[1]);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setTempMinValue(minValue);
        setTempMaxValue(maxValue);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [minValue, maxValue]);

  const handleMinSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value), tempMaxValue - step);
    setTempMinValue(newMin);
  };

  const handleMaxSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value), tempMinValue + step);
    setTempMaxValue(newMax);
  };

  const handleMinInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMin = Math.min(Number(e.target.value) || min, tempMaxValue - step);
    setTempMinValue(Math.max(newMin, min));
  };

  const handleMaxInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newMax = Math.max(Number(e.target.value) || max, tempMinValue + step);
    setTempMaxValue(Math.min(newMax, max));
  };

  const handleApply = () => {
    setMinValue(tempMinValue);
    setMaxValue(tempMaxValue);
    onChange?.([tempMinValue, tempMaxValue]);
    setIsOpen(false);
  };

  const handleReset = () => {
    setTempMinValue(min);
    setTempMaxValue(max);
  };

  const getMinPosition = () => ((tempMinValue - min) / (max - min)) * 100;
  const getMaxPosition = () => ((tempMaxValue - min) / (max - min)) * 100;

  return (
    <div className="relative flex-1" ref={dropdownRef}>
      <h3 className="font-medium text-[14px] mb-1">{label}</h3>

      {/* Input Field */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="bg-[#F2EBEE] p-3 border-[0.5px] border-[#000] rounded-lg flex items-center justify-between cursor-pointer"
      >
        <span className="text-sm text-gray-700">
          {currencySymbol}
          {minValue} - {currencySymbol}
          {maxValue}
        </span>
        <ChevronDown className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Popup */}
      {isOpen && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-medium text-[#621C43]">Drag to select</h3>
            <span className="font-medium text-sm bg-[#BD3D811F] p-1 px-2 rounded-xl">
              {currencySymbol}
              {tempMinValue} - {currencySymbol}
              {tempMaxValue}
            </span>
          </div>

          {/* Dual Range Slider */}
          <div className="relative mb-4 h-2">
            {/* Background Track */}
            <div className="absolute w-full h-2 bg-gray-200 rounded-full"></div>

            {/* Active Track */}
            <div
              className="absolute h-2 bg-[#A91D5E] rounded-full"
              style={{
                left: `${getMinPosition()}%`,
                right: `${100 - getMaxPosition()}%`,
              }}
            ></div>

            {/* Min Thumb */}
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={tempMinValue}
              onChange={handleMinSliderChange}
              className="
  absolute w-full h-2 appearance-none bg-transparent pointer-events-none
  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-white
  [&::-webkit-slider-thumb]:border-4
  [&::-webkit-slider-thumb]:border-[#A91D5E]
  [&::-webkit-slider-thumb]:shadow-[0_0_0_1px_#A91D5E]
  [&::-webkit-slider-thumb]:cursor-pointer

  [&::-moz-range-thumb]:pointer-events-auto
  [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5
  [&::-moz-range-thumb]:rounded-full
  [&::-moz-range-thumb]:bg-white
  [&::-moz-range-thumb]:border-4
  [&::-moz-range-thumb]:border-[#A91D5E]
  [&::-moz-range-thumb]:shadow-[0_0_0_1px_#A91D5E]
  [&::-moz-range-thumb]:cursor-pointer
"
              style={{ zIndex: tempMinValue > max - (max - min) / 4 ? 5 : 3 }}
            />

            {/* Max Thumb */}
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={tempMaxValue}
              onChange={handleMaxSliderChange}
              className="
  absolute w-full h-2 appearance-none bg-transparent pointer-events-none
  [&::-webkit-slider-thumb]:pointer-events-auto
  [&::-webkit-slider-thumb]:appearance-none
  [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
  [&::-webkit-slider-thumb]:rounded-full
  [&::-webkit-slider-thumb]:bg-white
  [&::-webkit-slider-thumb]:border-4
  [&::-webkit-slider-thumb]:border-[#A91D5E]
  [&::-webkit-slider-thumb]:shadow-[0_0_0_1px_#A91D5E]
  [&::-webkit-slider-thumb]:cursor-pointer

  [&::-moz-range-thumb]:pointer-events-auto
  [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5
  [&::-moz-range-thumb]:rounded-full
  [&::-moz-range-thumb]:bg-white
  [&::-moz-range-thumb]:border-4
  [&::-moz-range-thumb]:border-[#A91D5E]
  [&::-moz-range-thumb]:shadow-[0_0_0_1px_#A91D5E]
  [&::-moz-range-thumb]:cursor-pointer
"
              style={{ zIndex: 4 }}
            />
          </div>

          {/* Min and Max Input Fields */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-1">Min</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={tempMinValue}
                  onChange={handleMinInputChange}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A91D5E] focus:border-transparent"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-1">Max</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600">
                  {currencySymbol}
                </span>
                <input
                  type="number"
                  value={tempMaxValue}
                  onChange={handleMaxInputChange}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A91D5E] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={handleReset}
              className="px-4 py-2 text-[#A91D5E] hover:bg-gray-50 rounded-lg transition-colors"
            >
              Reset
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 bg-[#A91D5E] text-white rounded-xl hover:bg-[#8a1850] transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RangeSelector;
